import * as React from "react";
import { Slot } from "@radix-ui/react-slot";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { ChevronRight } from "@/assets";
import { ScrollAreaSafe } from "./scroll-area";

const SIDEBAR_COOKIE_NAME = "sidebar:state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

type SidebarContext = {
    state: "expanded" | "collapsed";
    open: boolean;
    setOpen: (open: boolean) => void;
    openMobile: boolean;
    setOpenMobile: (open: boolean) => void;
    isMobile: boolean;
    toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContext | null>(null);

function useSidebar() {
    const context = React.use(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider.");
    }

    return context;
}

const SidebarProvider = ({
    defaultOpen = true,
    open: openProp,
    onOpenChange: setOpenProp,
    className,
    style,
    children,
    ...props
}: React.ComponentProps<"div"> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React.useState(false);

    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp ?? _open;
    const setOpen = React.useCallback(
        (value: boolean | ((value: boolean) => boolean)) => {
            const openState = typeof value === "function" ? value(open) : value;
            if (setOpenProp) {
                setOpenProp(openState);
            } else {
                _setOpen(openState);
            }

            // This sets the cookie to keep the sidebar state.
            document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
        },
        [setOpenProp, open],
    );

    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(() => {
        // return isMobile
        //     ? setOpenMobile((open) => !open)
        //     : setOpen((open) => !open);

        // Using only desktop for now
        return setOpen((open) => !open);
    }, [setOpen]);

    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
                (event.metaKey || event.ctrlKey)
            ) {
                event.preventDefault();
                toggleSidebar();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);

    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? "expanded" : "collapsed";

    const contextValue = React.useMemo<SidebarContext>(
        () => ({
            state,
            open,
            setOpen,
            isMobile,
            openMobile,
            setOpenMobile,
            toggleSidebar,
        }),
        [
            state,
            open,
            setOpen,
            isMobile,
            openMobile,
            setOpenMobile,
            toggleSidebar,
        ],
    );

    return (
        <SidebarContext value={contextValue}>
            <TooltipProvider delayDuration={0}>
                <div
                    style={
                        {
                            "--sidebar-width": SIDEBAR_WIDTH,
                            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
                            ...style,
                        } as React.CSSProperties
                    }
                    className={cn(
                        "group/sidebar-wrapper has-data-[variant=inset]:bg-elevation-surface-default flex min-h-svh w-full",
                        className,
                    )}
                    {...props}
                >
                    {children}
                </div>
            </TooltipProvider>
        </SidebarContext>
    );
};

const Sidebar = ({
    side = "left",
    variant = "sidebar",
    collapsible = "offcanvas",
    className,
    children,
    ...props
}: React.ComponentProps<"div"> & {
    side?: "left" | "right";
    variant?: "sidebar" | "floating" | "inset";
    collapsible?: "offcanvas" | "icon" | "none";
}) => {
    const { state } = useSidebar();

    if (collapsible === "none") {
        return (
            <div
                className={cn(
                    "bg-elevation-surface-default text-default-color flex h-full w-(--sidebar-width) flex-col",
                    className,
                )}
                {...props}
            >
                {children}
            </div>
        );
    }

    return (
        <div
            className="group peer text-default-color relative block h-full"
            data-state={state}
            data-collapsible={state === "collapsed" ? collapsible : ""}
            data-variant={variant}
            data-side={side}
        >
            {/* This is what handles the sidebar gap on desktop */}
            <div
                className={cn(
                    "relative h-full w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear",
                    "group-data-[collapsible=offcanvas]:w-0",
                    "group-data-[side=right]:rotate-180",
                    variant === "floating" || variant === "inset"
                        ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
                        : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
                )}
            />
            <div
                className={cn(
                    "border-subtle absolute inset-y-0 z-10 flex w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear",
                    side === "left"
                        ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
                        : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
                    // Adjust the padding for floating and inset variants.
                    variant === "floating" || variant === "inset"
                        ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
                        : "group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l",
                    className,
                )}
                {...props}
            >
                <div
                    data-sidebar="sidebar"
                    className="bg-elevation-surface-default group-data-[variant=floating]:border-subtlest flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm"
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

const SidebarTrigger = ({
    className,
    onClick,
    ...props
}: React.ComponentProps<typeof Button>) => {
    const { toggleSidebar, open } = useSidebar();

    return (
        <Button
            data-sidebar="trigger"
            appearance="subtle"
            className={cn(
                "hover: border-subtle bg-elevation-surface-raised-default hover:bg-elevation-surface-raised-hover active:bg-elevation-surface-raised-press absolute top-[15px] right-0 z-10 flex h-6 min-h-0 w-6 translate-x-1/2 items-center justify-center rounded-full border pr-0 pl-0 shadow-md transition-all",
                !open ? "-right-3" : "",
                className,
            )}
            onClick={(event) => {
                onClick?.(event);
                toggleSidebar();
            }}
            {...props}
        >
            <ChevronRight
                className={`transition-all ${open ? "rotate-180" : ""}`}
            />
            <span className="sr-only">Toggle Sidebar</span>
        </Button>
    );
};

const SidebarRail = ({
    className,
    ...props
}: React.ComponentProps<"button"> & {
    ref: React.RefObject<HTMLButtonElement>;
}) => {
    const { toggleSidebar } = useSidebar();

    return (
        <button
            data-sidebar="rail"
            aria-label="Toggle Sidebar"
            tabIndex={-1}
            onClick={toggleSidebar}
            title="Toggle Sidebar"
            className={cn(
                "hover:after:bg-elevation-surface-default-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex",
                "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
                "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
                "hover:group-data-[collapsible=offcanvas]:bg-elevation-surface-default group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full",
                "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
                "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
                className,
            )}
            {...props}
        />
    );
};

const SidebarInset = ({
    className,
    ...props
}: React.ComponentProps<"main">) => {
    return (
        <main
            className={cn(
                "bg-elevation-surface-default relative flex min-h-svh flex-1 flex-col",
                "peer-data-[variant=inset]:min-h-[calc(100svh-(--spacing(4)))] md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
                className,
            )}
            {...props}
        />
    );
};

const SidebarInput = ({
    className,
    ...props
}: React.ComponentProps<typeof Input> & {
    ref: React.RefObject<React.ElementRef<typeof Input>>;
}) => {
    return (
        <Input
            data-sidebar="input"
            className={cn(
                "bg-elevation-surface-default h-8 w-full shadow-none",
                className,
            )}
            {...props}
        />
    );
};

const SidebarHeader = ({
    className,
    ...props
}: React.ComponentProps<"div">) => {
    return (
        <div
            data-sidebar="header"
            className={cn("flex flex-col gap-2 p-2", className)}
            {...props}
        />
    );
};

const SidebarFooter = ({
    className,
    ...props
}: React.ComponentProps<"div">) => {
    return (
        <div
            data-sidebar="footer"
            className={cn("flex flex-col gap-2 p-2", className)}
            {...props}
        />
    );
};

const SidebarSeparator = ({
    className,
    ...props
}: React.ComponentProps<typeof Separator> & {
    ref: React.RefObject<React.ElementRef<typeof Separator>>;
}) => {
    return (
        <Separator
            data-sidebar="separator"
            className={cn(
                "bg-elevation-surface-default-border mx-2 w-auto",
                className,
            )}
            {...props}
        />
    );
};

const SidebarContent = ({
    className,
    ...props
}: React.ComponentProps<"div">) => {
    return (
        <ScrollAreaSafe>
            <div
                data-sidebar="content"
                className={cn(
                    "flex min-h-0 flex-1 flex-col gap-2 overflow-auto pl-4 group-data-[collapsible=icon]:overflow-hidden",
                    className,
                )}
                {...props}
            />
        </ScrollAreaSafe>
    );
};

const SidebarGroup = ({ className, ...props }: React.ComponentProps<"div">) => {
    return (
        <div
            data-sidebar="group"
            className={cn(
                "relative flex w-full min-w-0 flex-col p-2",
                className,
            )}
            {...props}
        />
    );
};

const SidebarGroupLabel = ({
    className,
    asChild = false,
    ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) => {
    const Comp = asChild ? Slot : "div";

    return (
        <Comp
            data-sidebar="group-label"
            className={cn(
                "rounded-default text-large font-bolder leading-default h-8 shrink-0 truncate px-2 outline-hidden transition-[margin,opa] duration-200 ease-linear [&>svg]:size-4 [&>svg]:shrink-0",
                "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
                className,
            )}
            {...props}
        />
    );
};

const SidebarGroupAction = ({
    className,
    asChild = false,
    ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) => {
    const Comp = asChild ? Slot : "button";

    return (
        <Comp
            data-sidebar="group-action"
            className={cn(
                "hover:text-sidebar-accent-foreground rounded-default text-default-color hover:bg-neutral-subtlest-hover absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
                // Increases the hit area of the button on mobile.
                "after:absolute after:-inset-2 md:after:hidden",
                "group-data-[collapsible=icon]:hidden",
                className,
            )}
            {...props}
        />
    );
};

const SidebarGroupContent = ({
    className,
    ...props
}: React.ComponentProps<"div">) => (
    <div
        data-sidebar="group-content"
        className={cn("text-default w-full", className)}
        {...props}
    />
);

const SidebarMenu = ({ className, ...props }: React.ComponentProps<"ul">) => (
    <ul
        data-sidebar="menu"
        className={cn(
            "pl-default flex w-full min-w-0 flex-col gap-1",
            className,
        )}
        {...props}
    />
);

const SidebarMenuItem = ({
    className,
    ...props
}: React.ComponentProps<"li">) => (
    <li
        data-sidebar="menu-item"
        className={cn("group/menu-item relative", className)}
        {...props}
    />
);

const SidebarMenuButton = ({
    asChild = false,
    isActive = false,
    tooltip,
    className,
    ...props
}: React.ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | React.ComponentProps<typeof TooltipContent>;
}) => {
    const Comp = asChild ? Slot : "button";
    const { isMobile, state } = useSidebar();

    const button = (
        <Comp
            data-sidebar="menu-button"
            data-active={isActive}
            className={cn(
                // aria-[current=page] is for when rendering a navlink that is active
                "peer/menu-button rounded-default p-default text-default hover:bg-neutral-subtlest-hover active:bg-neutral-subtlest-press aria-[current=page]:bg-selected-default aria-[current=page]:text-selected aria-[current=page]:hover:bg-selected-hover aria-[current=page]:active:bg-selected-press data-[active=true]:bg-selected-default data-[active=true]:text-selected data-[active=true]:hover:bg-selected-hover data-[state=open]:hover:bg-neutral-subtlest-hover data-[active=true]:active:bg-selected-press flex h-10 w-full items-center gap-2 overflow-hidden text-left font-bold outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
                className,
            )}
            {...props}
        />
    );

    if (!tooltip) {
        return button;
    }

    if (typeof tooltip === "string") {
        tooltip = { children: tooltip };
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent
                side="right"
                align="center"
                hidden={state !== "collapsed" || isMobile}
                {...tooltip}
            />
        </Tooltip>
    );
};

const SidebarMenuAction = ({
    className,
    asChild = false,
    showOnHover = false,
    ...props
}: React.ComponentProps<"button"> & {
    asChild?: boolean;
    showOnHover?: boolean;
}) => {
    const Comp = asChild ? Slot : "button";

    return (
        <Comp
            data-sidebar="menu-action"
            className={cn(
                "ring-sidebar-ring hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground text-default-color hover:bg-neutral-subtlest-hover absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
                // Increases the hit area of the button on mobile.
                "after:absolute after:-inset-2 md:after:hidden",
                "peer-data-[size=sm]/menu-button:top-1",
                "peer-data-[size=default]/menu-button:top-1.5",
                "peer-data-[size=lg]/menu-button:top-2.5",
                "group-data-[collapsible=icon]:hidden",
                showOnHover &&
                    "peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0",
                className,
            )}
            {...props}
        />
    );
};

const SidebarMenuBadge = ({
    className,
    ...props
}: React.ComponentProps<"div">) => (
    <div
        data-sidebar="menu-badge"
        className={cn(
            "text-default-color pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none",
            "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
            "peer-data-[size=sm]/menu-button:top-1",
            "peer-data-[size=default]/menu-button:top-1.5",
            "peer-data-[size=lg]/menu-button:top-2.5",
            "group-data-[collapsible=icon]:hidden",
            className,
        )}
        {...props}
    />
);

const skeletonWidth = "75%";
const SidebarMenuSkeleton = ({
    className,
    showIcon = false,
    ...props
}: React.ComponentProps<"div"> & { showIcon?: boolean }) => {
    // Random width between 50 to 90%.

    return (
        <div
            data-sidebar="menu-skeleton"
            className={cn(
                "rounded-default flex h-8 items-center gap-2 px-2",
                className,
            )}
            {...props}
        >
            {showIcon && (
                <Skeleton
                    className="size-4 rounded-md"
                    data-sidebar="menu-skeleton-icon"
                />
            )}
            <Skeleton
                className="h-4 max-w-(--skeleton-width) flex-1"
                data-sidebar="menu-skeleton-text"
                style={
                    { "--skeleton-width": skeletonWidth } as React.CSSProperties
                }
            />
        </div>
    );
};

const SidebarMenuSub = ({
    className,
    ...props
}: React.ComponentProps<"ul">) => (
    <ul
        data-sidebar="menu-sub"
        className={cn(
            "ml-narrow flex min-w-0 translate-x-px flex-col gap-1 px-2.5 py-0.5",
            "group-data-[collapsible=icon]:hidden",
            className,
        )}
        {...props}
    />
);

const SidebarMenuSubItem = ({ ...props }: React.ComponentProps<"li">) => (
    <li {...props} />
);

const SidebarMenuSubButton = ({
    asChild = false,
    isActive,
    className,
    ...props
}: React.ComponentProps<"a"> & { asChild?: boolean; isActive?: boolean }) => {
    const Comp = asChild ? Slot : "a";

    return (
        <Comp
            data-sidebar="menu-sub-button"
            data-active={isActive}
            className={cn(
                "rounded-default text-default-color hover:bg-neutral-subtlest-hover active:bg-neutral-subtlest-press flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden px-2 outline-hidden disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
                "aria-[current=page]:bg-selected-default aria-[current=page]:text-selected aria-[current=page]:hover:bg-selected-hover aria-[current=page]:active:bg-selected-press data-[active=true]:bg-selected-default data-[active=true]:text-selected data-[active=true]:hover:bg-selected-hover",
                "group-data-[collapsible=icon]:hidden",
                className,
            )}
            {...props}
        />
    );
};

export {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupAction,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInput,
    SidebarInset,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuBadge,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSkeleton,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarProvider,
    SidebarRail,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar,
};
